import { useEffect, useRef, useState } from 'react';
import { Chessground } from 'chessground';
import type { Api } from 'chessground/api';
import type { Config } from 'chessground/config';
import type { Color, Key } from 'chessground/types';
import type { DrawShape } from 'chessground/draw';
import 'chessground/assets/chessground.base.css';
import 'chessground/assets/chessground.brown.css';
import 'chessground/assets/chessground.cburnett.css';

export interface BoardMoveEvent {
  from: Key;
  to: Key;
  /** Pièce de promotion UCI (q/r/b/n). Présent uniquement si auto-promu en dame. */
  promotion?: 'q' | 'r' | 'b' | 'n';
}

export interface BoardProps {
  /**
   * FEN = source unique de vérité. Le composant ne maintient pas d'instance
   * `Chess` interne — toute mise à jour de position passe par cette prop.
   */
  fen?: string;
  orientation?: Color;
  /** Si true, drag-drop + click-click + clavier activés */
  interactive?: boolean;
  lastMove?: [Key, Key];
  /** Carte des coups légaux : from → liste de cases cibles */
  legalMoves?: Map<Key, Key[]>;
  /** Couleur autorisée à jouer (ignoré si interactive=false) */
  turnColor?: Color;
  /** Fired quand un coup légal est joué (drag, click ou clavier) */
  onMove?: (move: BoardMoveEvent) => void;
  /** Flèches/cercles décoratifs */
  arrows?: DrawShape[];
  /**
   * Si true, le moteur est en train de réfléchir : interactivité coupée
   * (movable.color/dests cleared via api.set) + spinner discret affiché au
   * coin du board. Évite que Léon ne tente un coup pendant que Stockfish/le
   * drill calcule le coup adversaire (bug A2 tablette MVP2.5).
   */
  engineThinking?: boolean;
}

const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/**
 * Heuristique de promotion : détecte un coup de pion qui atteint la 1ère ou
 * 8ème rangée et auto-promeut en dame ('q'). Suffit pour Léon ~1700 ELO ;
 * la sous-promotion (T/F/C) est un thème MVP4+.
 *
 * Sans cette heuristique, Chessground/Chess.js rejettent un coup `e7e8`
 * sans pièce de promotion → coup illégal → reducer ignore, le coup
 * reste visuellement en pre-move "en attente" qui ne s'acte jamais.
 */
function detectAutoPromotion(fen: string, from: Key, to: Key): 'q' | undefined {
  if (!to.endsWith('1') && !to.endsWith('8')) return undefined;
  const board = fen.split(' ')[0];
  if (!board) return undefined;
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
  const fileIdx = files.indexOf(from[0] as (typeof files)[number]);
  const rankIdx = 8 - Number(from[1]);
  if (fileIdx < 0 || rankIdx < 0 || rankIdx > 7) return undefined;
  const ranks = board.split('/');
  const rank = ranks[rankIdx];
  if (!rank) return undefined;
  let col = 0;
  for (const ch of rank) {
    if (/[1-8]/.test(ch)) {
      col += Number(ch);
      continue;
    }
    if (col === fileIdx) {
      return ch.toLowerCase() === 'p' ? 'q' : undefined;
    }
    col += 1;
    if (col > fileIdx) return undefined;
  }
  return undefined;
}

/**
 * Wrapper Chessground React.
 *
 * Supporte :
 *  - drag-drop natif Chessground
 *  - click-click (built-in)
 *  - input clavier : focus le board, tape « e2e4 » puis Entrée → coup joué
 *
 * Highlight :
 *  - dernier coup : bordure --acid-500 25% (CSS via chessground.base.css)
 *  - coups légaux : --paper-50 15% (props.movable.dests)
 *
 * Contraintes MVP2.5 T1 (bug A2 tablette) :
 *  - **FEN = source unique de vérité.** Pas d'instance `Chess` interne.
 *  - `touch-action: none` sur le wrapper Chessground → empêche scroll/zoom
 *    natif iOS pendant un drag de pièce.
 *  - `disableContextMenu: true` dans config Chessground → coupe le menu
 *    contextuel iOS sur long-press.
 *  - `engineThinking` → `viewOnly: true` + spinner discret au coin du board.
 */
export function Board({
  fen,
  orientation = 'white',
  interactive = false,
  lastMove,
  legalMoves,
  turnColor,
  onMove,
  arrows,
  engineThinking = false,
}: BoardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<Api | null>(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  // Stocke le dernier FEN reçu pour pouvoir lire la pièce à promouvoir
  // depuis le callback `movable.events.after` (qui n'a pas accès aux props).
  const fenRef = useRef(fen ?? INITIAL_FEN);
  fenRef.current = fen ?? INITIAL_FEN;
  const [keyboardBuf, setKeyboardBuf] = useState('');

  // Indique si l'interactivité doit être coupée (read-only logique).
  // - !interactive : call-site veut un board read-only (spectateur, replay).
  // - engineThinking : engine réfléchit, on attend (bug A2 tablette MVP2.5).
  //
  // F-3 audit : on N'UTILISE PLUS la prop `viewOnly` de Chessground. La doc
  // officielle (Phase A §1.4 ligne 105) précise que `api.set()` ignore
  // `viewOnly` — donc une fois monté en viewOnly:true, le board reste stuck
  // non-interactive même après un `set({viewOnly:false})`. À la place, on
  // coupe l'interaction via `movable.color: undefined + movable.dests: empty
  // Map`. Pattern officiel Phase A §1.4 ligne 121.
  const effectiveViewOnly = !interactive || engineThinking;

  // Mount + unmount Chessground UNE seule fois. Recréer l'instance à chaque
  // changement de prop casse les touch events tablette (le destroy intercepte
  // le touchend avant que `movable.events.after` ne se déclenche, Chessground
  // place alors la pièce en pre-move visuel jamais validé).
  useEffect(() => {
    if (!containerRef.current) return;
    const moveEvents: NonNullable<Config['movable']>['events'] = {
      after: (orig: Key, dest: Key) => {
        const promotion = detectAutoPromotion(fenRef.current, orig, dest);
        const move: BoardMoveEvent = promotion
          ? { from: orig, to: dest, promotion }
          : { from: orig, to: dest };
        onMoveRef.current?.(move);
      },
    };
    const movable: NonNullable<Config['movable']> = effectiveViewOnly
      ? { free: false, showDests: false, dests: new Map(), events: moveEvents }
      : {
          free: false,
          showDests: true,
          ...(turnColor ? { color: turnColor } : {}),
          ...(legalMoves ? { dests: legalMoves } : {}),
          events: moveEvents,
        };
    const config: Config = {
      fen: fenRef.current,
      orientation,
      // P2 vague C'.1 : Chessground exige `state.turnColor === piece.color`
      // (en plus de `movable.color === piece.color`) pour autoriser un drag
      // via `canMove()` (chessground/dist/board.js ligne 220-233). Sans
      // turnColor top-level, Chessground utilise le default 'white' au mount
      // puis flip en interne à chaque coup UI — désynchronisation avec la
      // FEN dès que la réalité reducer (post-MOVE_ENGINE) avance d'un
      // demi-coup non vu par Chessground. Bug Léon Galaxy Tab 2026-05-17 :
      // après coup engine, state.turnColor reste à la couleur engine alors
      // que la nouvelle FEN dit Léon-au-trait → drag silencieux.
      ...(turnColor ? { turnColor } : {}),
      coordinates: true,
      // F-3 : on ne touche JAMAIS à viewOnly de Chessground. Si on mount avec
      // viewOnly:true, le board reste stuck non-interactive à vie (api.set
      // l'ignore). Toujours viewOnly:false ; l'interactivité réelle se gère
      // via `movable.color` + `movable.dests` (toggleables via api.set).
      viewOnly: false,
      // Coupe le menu contextuel iOS sur long-press (bug A2 tablette).
      disableContextMenu: true,
      animation: { duration: 200 },
      drawable: { enabled: false, autoShapes: arrows ?? [] },
      ...(lastMove ? { lastMove } : {}),
      movable,
      // Coupe explicitement les pre-moves : sans ça, si Chessground ne peut
      // pas matcher un coup à `movable.dests` (race conditions tablette ou
      // dests vides), il bascule en pre-move visuel "en attente" qui ne
      // s'acte jamais — le coup semble joué mais ne se mémorise pas.
      premovable: { enabled: false },
      predroppable: { enabled: false },
      draggable: { enabled: true, deleteOnDropOff: false },
    };
    apiRef.current = Chessground(containerRef.current, config);
    return () => {
      apiRef.current?.destroy();
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Propage les changements de props via l'API impérative (`set()` non
  // destructif). Les options `movable.events.after` set au mount sont
  // préservées car `set()` fait un merge non-destructif. `exactOptionalPropertyTypes`
  // CONVENTIONS §6 → construit les sous-objets conditionnellement plutôt que
  // de passer `undefined` (non assignable aux unions `"black" | "white" | "both"`).
  //
  // F-3 : `viewOnly` est volontairement absent du patch — `api.set()`
  // l'ignore en silence (Phase A §1.4 ligne 105). L'interactivité réelle
  // dépend de `movable.color` + `movable.dests` ci-dessous.
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    const movable: Config['movable'] = effectiveViewOnly
      ? { free: false, showDests: false, dests: new Map() }
      : {
          free: false,
          showDests: true,
          dests: legalMoves ?? new Map(),
          ...(turnColor ? { color: turnColor } : {}),
        };
    const patch: Config = {
      fen: fen ?? INITIAL_FEN,
      orientation,
      // P2 vague C'.1 : turnColor top-level SUR CHAQUE patch — sans ça,
      // `state.turnColor` reste figé à la valeur post-dernier-coup-UI
      // Chessground (qui flip via baseMove), et le canMove() suivant
      // refuse les drags légaux car state.turnColor !== piece.color.
      // Bug Léon Galaxy Tab 2026-05-17 : 1er coup OK (turnColor default
      // 'white' matche), engine joue → flip → 2ème coup blocked. Voir
      // commentaire mount config ci-dessus pour le détail.
      ...(turnColor ? { turnColor } : {}),
      drawable: { enabled: false, autoShapes: arrows ?? [] },
      movable,
      ...(lastMove ? { lastMove } : {}),
    };
    api.set(patch);
  }, [fen, orientation, effectiveViewOnly, lastMove, legalMoves, turnColor, arrows]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!interactive || engineThinking) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const m = keyboardBuf.match(/^([a-h][1-8])([a-h][1-8])$/i);
      if (m && m[1] && m[2] && apiRef.current) {
        const from = m[1].toLowerCase() as Key;
        const to = m[2].toLowerCase() as Key;
        const dests = apiRef.current.state.movable.dests;
        if (dests?.get(from)?.includes(to)) {
          apiRef.current.move(from, to);
          const promotion = detectAutoPromotion(fenRef.current, from, to);
          const move: BoardMoveEvent = promotion
            ? { from, to, promotion }
            : { from, to };
          onMoveRef.current?.(move);
        }
      }
      setKeyboardBuf('');
      return;
    }
    if (/^[a-h1-8]$/i.test(e.key)) {
      setKeyboardBuf((b) => (b + e.key.toLowerCase()).slice(-4));
    }
    if (e.key === 'Backspace') {
      setKeyboardBuf((b) => b.slice(0, -1));
    }
    if (e.key === 'Escape') {
      setKeyboardBuf('');
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        tabIndex={interactive && !engineThinking ? 0 : -1}
        onKeyDown={handleKeyDown}
        aria-label="Échiquier interactif (glisser, cliquer ou clavier a1a2)"
        className="relative aspect-square w-full max-w-[560px] overflow-hidden rounded-xl border border-ink-700 outline-none focus:border-acid-500"
      >
        <div
          ref={containerRef}
          data-testid="board-chessground"
          // `touch-action: none` empêche scroll/zoom natif iOS pendant un
          // drag de pièce sur tablette (bug A2 MVP2.5). Inline style ET
          // utility Tailwind `touch-none` : garantit que la valeur n'est
          // jamais purgée par PurgeCSS et reste visible dans le DOM pour
          // les tests Vitest (vérification du style inline).
          style={{ touchAction: 'none' }}
          className="h-full w-full touch-none"
        />
        {engineThinking && (
          <div
            data-testid="board-engine-spinner"
            role="status"
            aria-label="Le moteur réfléchit"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-ink-900/75 backdrop-blur-sm"
          >
            <svg
              className="h-4 w-4 animate-spin text-acid-500"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </div>
      {interactive && !engineThinking && keyboardBuf.length > 0 && (
        <p className="font-mono text-body-sm text-acid-500" aria-live="polite">
          {keyboardBuf}
          <span className="ml-2 text-ink-300">(Entrée pour valider, Échap pour annuler)</span>
        </p>
      )}
    </div>
  );
}
